import { json } from "react-router-dom";
import { getOrganization, userLogin } from "../services/auth.service";
import { getTemplatePlan } from "../services/plans-template.service";
import { getOrganizationPlan, getOrganizationByIdPlan } from "../services/organizations.service";
import { getPlanApprovals, getPlans } from "../services/plans.service";
import { getAgenda, getLastAnnotation } from "../services/agendas.service";
import { getAuthToken, redirectToLogin, setAuthToken, updateAuthToken } from "./common.helper";
import { getTemplatesPlansContentPresetEditor } from "../services/plans-template-content-preset-editor.service";

const currentSession = {};
let pendingSessionPromise = null;

/**
 * Check Role Owner
 * @returns
 */
export const authSession = async ({ params }) => {
	localStorage.removeItem("sessionUser");
	localStorage.removeItem("session_piano");

	const { sessionToken } = params;
	updateAuthToken(sessionToken);
	const res = await userLogin();

	if (res?.id && ["reseller", "admin", "organization"].includes(res?.role)) {
		setAuthToken(sessionToken);
		return { user: res.data };
	}

	return { notSession: true };
};

/**
 * Check session
 * @returns
 */
export const checkSession = async () => {
	if (currentSession?.user) {
		return { user: currentSession.user };
	}

	if (pendingSessionPromise) {
		return pendingSessionPromise;
	}

	const token = getAuthToken();

	if (!token) {
		redirectToLogin();
		return { notSession: true };
	}

	updateAuthToken(token);

	pendingSessionPromise = (async () => {
		try {
			const res = await userLogin();

			if (res?.id) {
				currentSession.user = res;
				return { user: res };
			}
			return { notSession: true };
		} catch {
			return { notSession: true };
		} finally {
			pendingSessionPromise = null;
		}
	})();

	return pendingSessionPromise;
};

/**
 * Check plan exist
 * @returns
 */
export const templatePlanPresetsLoader = async ({ params }) => {
	const plan = await getTemplatePlan(
		{
			id: params.id,
			include: ["requirementStatuses"],
		},
		undefined,
		() => {
			throw new Response("Not Found", { status: 404 });
		}
	);

	return {
		templatePlan: plan.data,
		statuses: plan.data.requirement_statuses || [],
	};
};

/**
 * Check plan exist
 * @returns
 */
export const organizationPlanPresetsLoader = async ({ params }) => {
	const body = {
		id: params.id,
		idPlan: params.idPlan,
		include: ["templatePlan"],
		append: ["organization_requirements_statuses"],
	};

	const plan = await getOrganizationPlan(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	return {
		templatePlan: plan?.data?.template_plan,
		statuses: plan.data.organization_requirements_statuses || [],
	};
};

/**
 * Check plan exist
 * @returns
 */
export const checkPlanTemplate = async ({ params }) => {
	const plan = await getTemplatePlan(
		{
			id: params.id,
			include: [
				"templateChapters",
				"templateChapters.templateChapterContents",
				"templateChapterContentsCount",
				"templateChapters.templateGoals",
				"templateGoalsCount",
				"templateChapters.templateGoals.templateRequirements",
				"templateRequirementsCount",
				"requirementStatuses",
			],
		},
		undefined,
		() => {
			throw new Response("Not Found", { status: 404 });
		}
	);

	return plan.data;
};

/**
 * Check plan exist
 * @returns
 */
export const getPlanTemplate = async ({ params }) => {
	const plan = await getTemplatePlan(
		{
			id: params.id,
			include: [
				"templateChaptersCount",
				"templateChapterContentsCount",
				"templateGoalsCount",
				"templateRequirementsCount",
				"requirementStatuses",
				"tags",
			],
			append: ["template_goals_statuses", "template_requirements_statuses"],
		},
		undefined,
		() => {
			throw new Response("Not Found", { status: 404 });
		}
	);

	return plan.data;
};

/**
 * TODO: remove after content presets implementation
 * Check plan exist
 * @returns
 */
export const loaderCopyTemplate = async ({ params }) => {
	const body = {
		id: params.idCorp,
		idPlan: params.idPlan,
		include: [
			"organization",
			"templatePlan",
			"organizationChapters",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"organizationChapters.templateChapter",
			"organizationChapters.organizationChapterContents",
			"organizationChapters.organizationChapterContents.templateChapterContent",
			"organizationChapters.organizationGoals",
			"organizationChapters.organizationGoals.templateGoal",
			"organizationChapters.organizationGoals.organizationRequirements",
			"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
		],
	};

	const plan = await getOrganizationPlan(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	return { plan: plan.data };
};

/**
 * Check plan exist
 * @returns
 */
export const loaderTemplatesPlansContentPresetEditor = async ({ params }) => {
	const body = {
		templatePlan: params.templatePlan,
		contentPreset: params.contentPreset,
	};

	const plan = await getTemplatesPlansContentPresetEditor(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	let organization;
	if (params?.idOrganization && params?.idOrganizationPlan) {
		organization = await getOrganizationByIdPlan(
			{
				idOrganization: params.idOrganization,
				idOrganizationPlan: params.idOrganizationPlan,
				include: ["organization"],
			},
			undefined,
			() => {
				throw new Response("Not Found", { status: 404 });
			}
		);
	}

	return {
		loaderData: { plan: plan.data, organization: organization?.data ?? {} },
	};
};

/**
 * Check working plan exist
 * @returns
 */
export const loaderEntityDetail = async ({ params }) => {
	const body = {
		id: params.id,
		include: [
			"organizationChaptersCount",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"templatePlan",
		],
		filter: {
			"templatePlan.working": true,
		},
		append: [
			"organization_requirements_completion_percentage",
			"organization_requirements_statuses",
			"template_goals_statuses",
			"template_requirements_statuses",
			"completion_percentage",
			"organization_chapter_contents_statuses",
			"attachments_count",
			"requirements_attachments_count",
		],
	};

	const organization = await getOrganization({ id: params.id }, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	try {
		// Fetch Api
		const workingPlans = await getPlans(body, undefined, () => {
			throw new Response("Not Found", { status: 200 });
		});

		return { organization: organization.data, workingPlans: workingPlans.data };
	} catch (error) {
		return { organization: organization.data };
	}
};

/**
 * Check working plan exist
 * @returns
 */
export const loaderEntityPlan = async ({ params }) => {
	const body = {
		id: params.id,
		idPlan: params.idPlan,
		include: [
			"agenda",
			"organization",
			"templatePlan",
			"organizationChapters",
			"organizationChaptersCount",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"organizationChapters.templateChapter",
			"organizationChapters.organizationChapterContents",
			"organizationChapters.organizationChapterContents.templateChapterContent",
			"organizationChapters.organizationGoals",
			"organizationChapters.organizationGoals.templateGoal",
			"organizationChapters.organizationGoals.organizationRequirements",
			"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
		],
		append: [
			"organization_requirements_completion_percentage",
			"organization_requirements_statuses",
			"template_goals_statuses",
			"template_requirements_statuses",
			"completion_percentage",
			"organization_chapter_contents_statuses",
		],
	};

	const plan = await getOrganizationPlan(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	const approvals = await getPlanApprovals(
		{ idCorp: params.id, idPlan: params.idPlan },
		undefined,
		() => {
			/* throw new Response("Not Found", { status: 404 }); */
		}
	);

	return {
		planData: plan.data,
		approvalsList: approvals?.data ? approvals.data : [],
	};
};

/**
 * Check Plan to compile
 * @returns
 */
export const loaderCompilePlan = async ({ params }) => {
	const body = {
		id: params.id,
		idPlan: params.idPlan,
		include: [
			"agenda",
			"organization",
			"templatePlan",
			"organizationChapters",
			"organizationChaptersCount",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
		],
		append: ["organization_requirements_statuses"],
	};

	const plan = await getOrganizationPlan(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	return plan.data;
};

/**
 * Check Plan to compile
 * @returns
 */
export const loaderListAgenda = async ({ params }) => {
	const lastAnnotation = await getLastAnnotation();

	return lastAnnotation?.data?.id ? lastAnnotation.data : null;
};

/**
 * Check Plan to compile
 * @returns
 */
export const loaderAgendaDetail = async ({ params }) => {
	const body = {
		id: params.id,
		idCorp: params.idCorp,
	};

	const agenda = await getAgenda(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	return { ...agenda.data, organization_id: params.idCorp };
};

/**
 * User redirect
 * @param {*} param0
 * @returns
 */
export const redirectUser = async ({ params }) => {
	const token = getAuthToken();

	if (token) {
		updateAuthToken(token);

		const res = await userLogin();

		if (res?.id) {
			return { user: res };
		}

		return { noSession: true };
	}

	return { noSession: true };
};

/**
 * Check Admin
 * @param {*} param0
 * @returns
 */
export const checkAdmin = async (data) => {
	if (currentSession?.user) {
		if (currentSession.user.role === "admin") {
			return null;
		}
		throw json({ message: "Unauthorized" }, { status: 403 });
	}

	const sessionData = await checkSession(data);

	if (sessionData?.user?.role === "admin") {
		return null;
	}

	throw json({ message: "No session" }, { status: 404 });
};

/**
 * Check Reseller
 * @param {*} param0
 * @returns
 */
export const checkReseller = async (data) => {
	if (currentSession?.user) {
		if (currentSession.user.role === "reseller") {
			return null;
		}
		throw json({ message: "Unauthorized" }, { status: 403 });
	}

	const sessionData = await checkSession(data);

	if (sessionData?.user?.role === "reseller") {
		return null;
	}

	throw json({ message: "No session" }, { status: 404 });
};

/**
 * Check organization
 * @param {*} param0
 * @returns
 */
export const checkOrganization = async (data) => {
	if (currentSession?.user) {
		if (currentSession.user.role === "organization") {
			return null;
		}
		throw json({ message: "Unauthorized" }, { status: 403 });
	}

	const sessionData = await checkSession(data);

	if (sessionData?.user?.role === "organization") {
		return null;
	}

	throw json({ message: "No session" }, { status: 404 });
};

export const onganizationDetail = async ({ params }) => {
	let user = null;

	if (currentSession?.user) {
		user = currentSession.user;
	} else {
		const sessionData = await checkSession({ params });
		if (sessionData?.user) {
			user = sessionData?.user;
		} else {
			throw json({ message: "No session" }, { status: 404 });
		}
	}

	const body = {
		id: user.organization_id,
		include: [
			"organizationChaptersCount",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"templatePlan",
		],
		filter: {
			"templatePlan.working": true,
		},
		append: [
			"organization_requirements_completion_percentage",
			"organization_requirements_statuses",
			"template_goals_statuses",
			"template_requirements_statuses",
			"completion_percentage",
			"organization_chapter_contents_statuses",
			"attachments_count",
			"requirements_attachments_count",
		],
	};

	try {
		// Fetch Api
		const workingPlans = await getPlans(body, undefined, () => {
			throw new Response("Not Found", { status: 200 });
		});

		return {
			organization: { id: user.organization_id },
			workingPlans: workingPlans.data,
		};
	} catch (error) {
		return { organization: { id: user.organization_id } };
	}
};

/**
 * Check working plan exist
 * @returns
 */
export const loaderOrganizationPlan = async ({ params }) => {
	let user = null;

	if (currentSession?.user) {
		user = currentSession.user;
	} else {
		const sessionData = await checkSession({ params });
		if (sessionData?.user) {
			user = sessionData?.user;
		} else {
			throw json({ message: "No session" }, { status: 404 });
		}
	}

	const body = {
		id: user.organization_id,
		idPlan: params.idPlan,
		include: [
			"agenda",
			"organization",
			"templatePlan",
			"organizationChapters",
			"organizationChaptersCount",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"organizationChapters.templateChapter",
			"organizationChapters.organizationChapterContents",
			"organizationChapters.organizationChapterContents.templateChapterContent",
			"organizationChapters.organizationGoals",
			"organizationChapters.organizationGoals.templateGoal",
			"organizationChapters.organizationGoals.organizationRequirements",
			"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
		],
		append: [
			"organization_requirements_completion_percentage",
			"organization_requirements_statuses",
			"template_goals_statuses",
			"template_requirements_statuses",
			"completion_percentage",
			"organization_chapter_contents_statuses",
		],
	};

	const plan = await getOrganizationPlan(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	const approvals = await getPlanApprovals(
		{ idCorp: user.organization_id, idPlan: params.idPlan },
		undefined,
		() => {
			/* throw new Response("Not Found", { status: 404 }); */
		}
	);

	return {
		planData: plan.data,
		approvalsList: approvals?.data ? approvals.data : [],
	};
};

/**
 * Check Plan to compile
 * @returns
 */
export const loaderOrganizationCompile = async ({ params }) => {
	let user = null;

	if (currentSession?.user) {
		user = currentSession.user;
	} else {
		const sessionData = await checkSession({ params });
		if (sessionData?.user) {
			user = sessionData?.user;
		} else {
			throw json({ message: "No session" }, { status: 404 });
		}
	}

	const body = {
		id: user.organization_id,
		idPlan: params.idPlan,
		include: [
			"agenda",
			"organization",
			"templatePlan",
			"organizationChapters",
			"organizationChaptersCount",
			"organizationChapterContentsCount",
			"organizationGoalsCount",
			"organizationRequirementsCount",
			"organizationChapters.organizationGoals.organizationRequirements.templateRequirement",
		],
		append: ["organization_requirements_statuses"],
	};

	const plan = await getOrganizationPlan(body, undefined, () => {
		throw new Response("Not Found", { status: 404 });
	});

	return plan.data;
};
